import { Loader2, RotateCcw, Search } from 'lucide-react';
import { type FormEvent } from 'react';
import { cn } from '@/lib/cn';
import type { TimeZoneMode } from '@/lib/format';
import type { BusinessClientSummary } from '@/types';
import { ClientSelect } from '@/features/bet-inspection/components/ClientSelect';
import { fromHourInput, toHourInput } from '@/features/player-reports/lib/time';

export interface InspectionFilters {
  psid: string;
  playerId: string;
  /** UTC instants — the tz toggle re-renders them, never reinterprets. */
  from: number;
  to: number;
}

export function FilterBar({
  value,
  onChange,
  onSearch,
  onReset,
  loading,
  clients,
  clientsLoading,
  clientsError,
  tz,
}: {
  value: InspectionFilters;
  onChange: (patch: Partial<InspectionFilters>) => void;
  onSearch: () => void;
  onReset: () => void;
  loading: boolean;
  clients: BusinessClientSummary[];
  clientsLoading: boolean;
  clientsError: boolean;
  tz: TimeZoneMode;
}) {
  const canSearch = Boolean(value.psid && value.playerId.trim() && value.to > value.from);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (canSearch) onSearch();
  };

  return (
    <form onSubmit={submit} className="flex flex-wrap items-center gap-1.5 border-b border-hairline px-4 py-2.5">
      <ClientSelect
        value={value.psid}
        onChange={(psid) => onChange({ psid })}
        clients={clients}
        loading={clientsLoading}
        error={clientsError}
      />
      <Field label="player_id" value={value.playerId} onChange={(playerId) => onChange({ playerId })} width="w-[260px]" />
      <HourField label="from" ms={value.from} tz={tz} onChange={(from) => onChange({ from })} width="w-[195px]" />
      <HourField label="to" ms={value.to} tz={tz} onChange={(to) => onChange({ to })} width="w-[185px]" />

      <button
        type="button"
        onClick={onReset}
        className="inline-flex items-center gap-1.5 border border-hairline px-2.5 py-1.5 font-condensed text-[11px] font-semibold uppercase tracking-wider text-text-dim hover:border-hairline-strong hover:text-text"
      >
        <RotateCcw size={12} /> Reset
      </button>
      <button
        type="submit"
        disabled={!canSearch || loading}
        className={cn(
          'inline-flex items-center gap-1.5 border border-accent/40 bg-accent/10 px-3 py-1.5',
          'font-condensed text-[11px] font-semibold uppercase tracking-wider text-accent',
          'hover:bg-accent/20 disabled:cursor-not-allowed disabled:opacity-40',
        )}
      >
        {loading ? <Loader2 size={12} className="animate-spin" /> : <Search size={12} />}
        Inspect
      </button>

      <span className="ml-auto hidden text-[11px] text-text-faint xl:block">
        A player id is unique within its project. Times are UTC.
      </span>
    </form>
  );
}

function HourField({
  label,
  ms,
  tz,
  onChange,
  width,
}: {
  label: string;
  ms: number;
  tz: TimeZoneMode;
  onChange: (ms: number) => void;
  width: string;
}) {
  return (
    <label className={cn('relative block', width)}>
      <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 font-condensed text-[9px] uppercase tracking-wider text-text-faint">
        {label}
      </span>
      <input
        type="datetime-local"
        step={3600}
        value={toHourInput(ms, tz)}
        onChange={(e) => {
          const next = fromHourInput(e.target.value, tz);
          if (next !== null) onChange(next);
        }}
        className={cn(
          'w-full border border-hairline bg-surface-sunken py-1.5 pr-2',
          'font-mono text-[12px] text-text hover:border-hairline-strong focus:border-accent focus:outline-none',
          '[color-scheme:dark]',
        )}
        style={{ paddingLeft: `${label.length * 5 + 16}px` }}
      />
    </label>
  );
}

function Field({
  label,
  value,
  onChange,
  width,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  width: string;
}) {
  return (
    <label className={cn('relative block', width)}>
      <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 font-condensed text-[9px] uppercase tracking-wider text-text-faint">
        {label}
      </span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        spellCheck={false}
        autoComplete="off"
        className={cn(
          'w-full border border-hairline bg-surface-sunken py-1.5 pr-2',
          'font-mono text-[12px] text-text placeholder:text-text-faint',
          'hover:border-hairline-strong focus:border-accent focus:outline-none',
        )}
        style={{ paddingLeft: `${label.length * 5 + 16}px` }}
      />
    </label>
  );
}
